import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertTriangle,
  IndianRupee,
  CheckCircle,
  ArrowRight,
} from "lucide-react";
import { useEffect, useState } from "react";
import { confirmLoanPrepayment } from "@/services/adminService";
import { toast } from "sonner";

// Utility function to format currency for INR
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Math.round(amount));
};

// Bank-grade EMI formula (reducing balance)
const calculateEmi = (principal, monthlyRate, months) => {
  const P = Number(principal);
  const r = Number(monthlyRate);
  const n = Number(months);

  if (!P || P <= 0) return 0;
  if (!n || n <= 0) return 0;

  // 0% interest
  if (!r || r === 0) return Math.round(P / n);

  const pow = Math.pow(1 + r, n);
  const emi = (P * r * pow) / (pow - 1);

  if (!Number.isFinite(emi)) return 0;

  return Math.round(emi);
};

export default function LoanPrepaymentWizard({
  open,
  onClose,
  loan,
  onConfirm,
}) {
  const [step, setStep] = useState(1);
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!open) {
      setStep(1);
      setAmount("");
      setError("");
      setPreview(null);
    }
  }, [open]);

  if (!loan) return null;

  const outstanding = Number(loan.outstanding_amount ?? loan.amount ?? 0);
  const remainingEmis = Number(
    loan.remaining_emis ?? loan.tenure_months - (loan.paid_emis || 0)
  );

  // Use the actual EMI from the loan record (Source of Truth)
  const currentEmi = Number(loan.monthly_emi ?? 0);

  const annualRate = Number(loan.interest_rate ?? 0);
  const monthlyRate = annualRate / 12 / 100;

  const calculatePreview = () => {
    const prepay = Number(amount);

    const newPrincipal = outstanding - prepay;

    // Full closure edge case
    if (newPrincipal <= 0) {
      return {
        prepayment: prepay,
        originalOutstanding: outstanding,
        originalEmi: currentEmi,
        remainingEmis,
        newOutstanding: 0,
        newEmi: 0,
      };
    }

    const newEmi = calculateEmi(newPrincipal, monthlyRate, remainingEmis);

    return {
      prepayment: prepay,
      originalOutstanding: outstanding,
      originalEmi: currentEmi,
      remainingEmis,
      newOutstanding: Math.round(newPrincipal),
      newEmi,
    };
  };

  const validateStep = () => {
    setError("");

    if (step === 1) {
      const prepay = Number(amount);

      if (!prepay || prepay <= 0) {
        setError("Enter a valid prepayment amount greater than zero.");
        return false;
      }

      if (prepay > outstanding) {
        setError(
          `Prepayment cannot exceed the outstanding balance of ${formatCurrency(
            outstanding
          )}.`
        );
        return false;
      }

      if (!remainingEmis || remainingEmis <= 0) {
        setError(
          "This loan has no remaining EMIs. Prepayment is not applicable."
        );
        return false;
      }

      if (!currentEmi || currentEmi <= 0) {
        setError("Current EMI is missing/invalid for this loan.");
        return false;
      }

      const calc = calculatePreview();

      if (!calc) {
        setError("Unable to calculate prepayment preview.");
        return false;
      }

      // Allow progression only if EMI reduces (or if it's full closure)
      if (calc.newOutstanding > 0 && calc.newEmi >= calc.originalEmi) {
        setError(
          `Prepayment is too small to reduce EMI after rounding. Try a larger amount.`
        );
        return false;
      }

      setPreview(calc);
    }

    return true;
  };

  const next = () => {
    if (validateStep()) setStep((s) => s + 1);
  };

  const submit = async () => {
    try {
      const payload = {
        loan_id: loan._id,
        prepayment_type: "partial",
        amount: Number(amount),
        impact_mode: "reduce_emi",
      };

      const response = await confirmLoanPrepayment(payload);

      toast.success("Loan prepayment applied successfully");

      // Optional: pass updated loan back to parent
      if (onConfirm) {
        onConfirm(response.loan);
      }
      onClose(false);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to apply loan prepayment"
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      {/* DialogContent handles its own dark mode using the theme */}
      <DialogContent className="max-w-lg space-y-4">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            Loan Prepayment
            <span className="text-sm text-muted-foreground">
              Step {step} / 3
            </span>
          </DialogTitle>
          <DialogDescription>
            Current Outstanding Balance:{" "}
            <strong>{formatCurrency(outstanding)}</strong>
          </DialogDescription>
        </DialogHeader>

        {/* Error Alert: Dark Mode adjusted */}
        {error && (
          <div className="flex gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm dark:bg-red-950 dark:border-red-800 dark:text-red-300">
            <AlertTriangle className="h-5 w-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* STEP 1 */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="prepayment-amount">Prepayment Amount</Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="prepayment-amount"
                  type="number"
                  className="pl-9 text-lg py-6"
                  placeholder="e.g. 50000"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  min="1"
                  step="1000"
                />
              </div>
            </div>

            {/* Info Box: Dark Mode adjusted */}
            <div className="text-sm text-muted-foreground bg-blue-50 border border-blue-200 p-3 rounded-md space-y-1 dark:bg-blue-950 dark:border-blue-800">
              <p>
                <CheckCircle className="inline h-4 w-4 mr-1 text-blue-600 dark:text-blue-400" />
                Impact: EMI will reduce. Tenure remains fixed ({remainingEmis}{" "}
                months).
              </p>
              <p>
                Current EMI (as per loan record):{" "}
                <strong>{formatCurrency(currentEmi)}</strong>
              </p>
            </div>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && preview && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold border-b pb-2">
              Prepayment Impact Preview
            </h3>

            {/* Prepayment Amount Box: Dark Mode adjusted */}
            <div className="flex justify-between items-center border rounded-lg p-4 bg-yellow-50/50 dark:bg-yellow-900/50 dark:border-yellow-700">
              <p className="text-sm">Prepayment Amount:</p>
              <strong className="text-lg text-primary">
                {formatCurrency(preview.prepayment)}
              </strong>
            </div>

            {/* Before vs After Comparison Grid */}
            <div className="relative grid grid-cols-2 gap-4">
              {/* BEFORE Card: Dark Mode adjusted */}
              <div className="border p-4 rounded-md bg-white space-y-2 shadow-sm dark:bg-gray-800 dark:border-gray-700">
                <h4 className="font-bold text-gray-500 text-sm dark:text-gray-400">
                  BEFORE
                </h4>
                <p className="text-sm">Outstanding:</p>
                <strong className="text-lg text-red-600 dark:text-red-400">
                  {formatCurrency(preview.originalOutstanding)}
                </strong>
                <p className="text-sm">Current EMI (stored):</p>
                <strong className="text-lg text-red-600 dark:text-red-400">
                  {formatCurrency(preview.originalEmi)}
                </strong>
              </div>

              {/* AFTER Card: Dark Mode adjusted */}
              <div className="border p-4 rounded-md bg-green-50/70 space-y-2 shadow-lg dark:bg-green-900/70 dark:border-green-800">
                <h4 className="font-bold text-green-700 text-sm dark:text-green-300">
                  AFTER
                </h4>
                <p className="text-sm">New Outstanding:</p>
                <strong className="text-xl text-green-800 dark:text-green-200">
                  {formatCurrency(preview.newOutstanding)}
                </strong>
                <p className="text-sm">New EMI:</p>
                <strong className="text-xl text-green-800 dark:text-green-200">
                  {formatCurrency(preview.newEmi)}
                </strong>
                <p className="text-xs text-muted-foreground mt-2">
                  Tenure remains {preview.remainingEmis} EMIs.
                </p>
              </div>

              {/* Arrow: Dark Mode adjusted background */}
              <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-full p-1 shadow dark:bg-gray-900">
                <ArrowRight className="h-6 w-6 text-gray-400" />
              </div>
            </div>

            {/* Reduction Summary: Dark Mode adjusted text color */}
            <div className="border-t pt-4 mt-4 dark:border-gray-700">
              <p className="text-base font-semibold">
                Monthly EMI Reduction:
                <span className="text-green-600 ml-2 dark:text-green-400">
                  {formatCurrency(preview.originalEmi - preview.newEmi)}
                </span>
                <span className="text-sm text-muted-foreground ml-1">
                  per month
                </span>
              </p>
            </div>
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && preview && (
          <div className="space-y-4">
            {/* Warning Box: Dark Mode adjusted */}
            <div className="flex gap-2 bg-yellow-100 border border-yellow-300 p-4 rounded-md text-sm text-yellow-800 dark:bg-yellow-950 dark:border-yellow-800 dark:text-yellow-300">
              <AlertTriangle className="h-5 w-5 flex-shrink-0" />
              <span>
                Warning: This action is irreversible and will immediately affect
                loan ledger and subsequent payroll deductions.
              </span>
            </div>

            {/* Confirmation Box: Dark Mode adjusted */}
            <div className="border border-green-300 rounded-md p-4 text-sm space-y-3 bg-green-50 dark:bg-green-950 dark:border-green-800">
              <h4 className="flex items-center gap-2 font-bold text-lg text-green-700 dark:text-green-400">
                <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-500" />
                Final Confirmation Details
              </h4>

              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                <p className="text-muted-foreground">Prepayment:</p>
                <strong className="text-right">
                  {formatCurrency(preview.prepayment)}
                </strong>

                <p className="text-muted-foreground">Original EMI:</p>
                <strong className="text-right">
                  {formatCurrency(preview.originalEmi)}
                </strong>

                <p className="font-semibold text-green-700 dark:text-green-400">
                  New EMI:
                </p>
                <strong className="text-right text-xl text-green-700 dark:text-green-300">
                  {formatCurrency(preview.newEmi)}
                </strong>

                <p className="text-muted-foreground">Remaining EMIs (Fixed):</p>
                <strong className="text-right">{preview.remainingEmis}</strong>

                <p className="text-muted-foreground">New Outstanding:</p>
                <strong className="text-right">
                  {formatCurrency(preview.newOutstanding)}
                </strong>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="pt-4">
          {step > 1 && (
            <Button variant="outline" onClick={() => setStep((s) => s - 1)}>
              Back
            </Button>
          )}

          {step < 3 ? (
            <Button onClick={next} disabled={step === 1 && !amount}>
              Continue
            </Button>
          ) : (
            <Button
              className="bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800"
              onClick={submit}
            >
              Confirm Prepayment
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
